// The days a Planned or Time interval value covers - one rule for the screen's task status (taskStatus.js) and the
// Excel report's (excelReport/cellStatus.js): a task is This week from the first day and overdue only after the last.
import { formatDate } from './dateFormat';

/** A value as text, without the control characters an Excel file cannot hold (excelReport/baseSheet.js removes them there too). */
// eslint-disable-next-line no-control-regex
export const cleanText = (value) => (value === null || value === undefined ? '' : String(value).replace(/[\u0000-\u001F]/g, '').trim());

// Month names, English and the Danish ones that differ in their first three letters
const MONTHS = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, maj: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, okt: 9, nov: 10, dec: 11 };
const monthIndex = (name) => MONTHS[String(name).slice(0, 3).toLowerCase()];
const lastDay = (year, month) => new Date(year, month + 1, 0);
// The last month of each season, as the Seasons input has them (Winter runs into the next year); it starts two before
const SEASON_END = { spring: [4, 0], summer: [7, 0], autumn: [10, 0], fall: [10, 0], winter: [1, 1] };
// primo = the 1st-10th, medio = the 11th-20th, ultimo = the 21st to the last day of the month
const POSITION_DAYS = { primo: [1, 10], medio: [11, 20], ultimo: [21, null] };

/** The Monday of ISO week `week` of `year`. */
function isoWeekMonday(week, year) {
  const jan4 = new Date(year, 0, 4);
  return new Date(year, 0, 4 - ((jan4.getDay() + 6) % 7) + (week - 1) * 7);
}
const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

/**
 * The days a Planned or Time interval value covers, as { start, end } (local Dates) - or null when it cannot be told:
 * - a date (2026-05-04, 04-05-2026, 20th December 2024, ...): that day
 * - "primo|medio|ultimo <Month> <Year>": the 1st-10th, 11th-20th, 21st-last day of the month (the Seasons input gives these)
 * - "week <N> <Year>": that ISO week, Monday to Sunday
 * - "<Season> <Year>" (Spring, Summer, Autumn, Winter): its three months (Winter: December to February the year after)
 * - "<Month> <Year>", "<Month>/<Month> <Year>" (e.g. "April 2022", "Sept/Oct 2022"): the month(s)
 * Only for status: the date that is saved (planned_date, utils/planning.js plannedDateOf) is not this.
 */
export function period(value) {
  const s = cleanText(value);
  if (!s || /bbch/i.test(s)) return null;

  const shown = formatDate(s); // a date, written in any way the app reads, as DD-MM-YYYY
  const dmy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(shown);
  if (dmy) {
    const d = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
    return d.getMonth() === Number(dmy[2]) - 1 ? { start: d, end: d } : null;
  }
  const position = /^(primo|medio|ultimo)\s+([a-zæøå]+)\s+(\d{4})$/i.exec(s);
  if (position && monthIndex(position[2]) !== undefined) {
    const [year, month] = [Number(position[3]), monthIndex(position[2])];
    const [first, last] = POSITION_DAYS[position[1].toLowerCase()];
    return { start: new Date(year, month, first), end: last ? new Date(year, month, last) : lastDay(year, month) };
  }
  const week = /^week\s+(\d{1,2})\s+(\d{4})$/i.exec(s);
  if (week && Number(week[1]) >= 1 && Number(week[1]) <= 53) {
    const monday = isoWeekMonday(Number(week[1]), Number(week[2]));
    return { start: monday, end: addDays(monday, 6) };
  }
  const season = /^(spring|summer|autumn|fall|winter)\s+(\d{4})$/i.exec(s);
  if (season) {
    const [month, yearOffset] = SEASON_END[season[1].toLowerCase()];
    const end = lastDay(Number(season[2]) + yearOffset, month);
    return { start: new Date(end.getFullYear(), end.getMonth() - 2, 1), end };
  }
  const months = /^([a-zæøå]+)\.?(?:\s*\/\s*([a-zæøå]+)\.?)?\s+(\d{4})$/i.exec(s);
  if (months) {
    const [first, last] = [monthIndex(months[1]), monthIndex(months[2] ?? months[1])];
    // (two months the wrong way round, e.g. "Dec/Jan 2022": the last one only, as its end always was)
    if (first !== undefined && last !== undefined) return { start: new Date(Number(months[3]), Math.min(first, last), 1), end: lastDay(Number(months[3]), last) };
  }
  return null;
}

/** The last day a Planned or Time interval value covers (see period), or null when it cannot be told. */
export const periodEnd = (value) => period(value)?.end ?? null;
